import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { ScreeningRuleProjection } from "@/lib/screeningSemantics";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * One Pathway version's screening diagnosis rules, with their effective meaning.
 *
 * A SEPARATE READ FROM THE VERSION, for `useSummaryExamplesQuery`' reason and
 * with its shape: the version read returns the stored bytes and must keep doing
 * exactly that — #16's export reproduces a stored identity from it — while this
 * returns the JOIN a client cannot make. What a diagnosis rule DOES depends on
 * the classification schema (frequently base canon's rather than this
 * version's), on base canon's own diagnosis at the same coordinate, and on the
 * pinned engine's defaults. All three live on the deployment's mount, behind
 * `library.read`, and none is derivable in the browser.
 *
 * ONE READ FOR EVERY LANGUAGE, unlike the Summary set: the response carries a
 * row per language the version stores a screening document in, each resolved
 * against its own pack language tree. A per-language key would fetch the same
 * body once per row.
 *
 * `enabled` on the version, so a surface that has not resolved a head yet does
 * not fetch a projection of `""`. The service independently refuses every
 * non-Pathway family with a field-addressed 422 rather than answering an empty
 * projection for the wrong asset type.
 */
export function useScreeningRulesQuery(
  assetId: string,
  version: string | null,
): UseQueryResult<ScreeningRuleProjection> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.screeningRules(assetId, version ?? ""),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/library/assets/{assetId}/screening-rules", {
          params: { path: { assetId }, query: { version: version as string } },
          signal,
        }),
      );
    },
    enabled: assetId.length > 0 && version !== null,
  });
}
